import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from '@dashai/lib/firebase';
import { chatStoreClient } from '@dashai/lib/chatStoreClient';
import { toChatSession } from '@dashai/lib/toChatSession';
import type { ChatSession } from '@dashai/types/dashai';

// How many recent chats the DashAI menu lists.
export const RECENT_CHAT_COUNT = 2;

// The chats the top bar's DashAI menu offers outside /ai, where no chat list
// is loaded. The server returns only the newest few, so opening the menu costs
// a few document reads. It fetches only when the menu asks, so a dashboard
// load costs nothing. Each open refreshes the list while still showing the
// last one, and a failed fetch leaves the menu without recent chats.
export function useRecentChats(workspace: string | null) {
  const queryClient = useQueryClient();
  // The list belongs to one user and one workspace, so a sign-in or a switch
  // reads a different cache entry rather than showing the previous one.
  const uid = auth.currentUser?.uid ?? null;

  const queryFor = useCallback(
    () => ({
      queryKey: ['recentChats', uid, workspace],
      queryFn: async (): Promise<ChatSession[]> => {
        const { sessions } = await chatStoreClient.recentSessions(workspace as string, RECENT_CHAT_COUNT);
        return sessions.map(toChatSession);
      },
      retry: false,
    }),
    [uid, workspace],
  );

  const { data, isFetching } = useQuery({ ...queryFor(), enabled: false });

  const load = useCallback(async () => {
    if (!workspace || !uid) return;
    await queryClient.prefetchQuery(queryFor());
  }, [queryClient, queryFor, uid, workspace]);

  // Loading means the first list for this user and workspace is on its way; a
  // refresh keeps showing the list it already has.
  return { recentChats: data ?? [], loading: data === undefined && isFetching, load };
}
